import { ReactElement } from 'react';
import Image from 'chat-ui/src/components/Image/Image';
import useTheme from 'chat-ui/src/hooks/useTheme';
import TelletLogo from '@/assets/tellet-logo-main.svg';
import TelletLogoWhite from '@/assets/tellet-logo-main-white.svg';
import EmpathyLogo from '@/assets/empathy-logo.svg';
import { ECompanyThemeName, LogoVariant } from '@/enums/enums';
import CustomLogo from '@/components/CustomLogo';
import { CreateThemeData } from '@/types';

interface UseCompanyLogoProps {
  themeData?: CreateThemeData;
  variant?: LogoVariant;
  className?: string;
  isFullPageLoader?: boolean;
}

interface UseCompanyLogoReturn {
  logo: ReactElement;
  isCustomLogoPresent: boolean;
}

const useCompanyLogo = ({
  themeData,
  variant = LogoVariant.DEFAULT,
  className = 'company-logo-default',
  isFullPageLoader = false,
}: UseCompanyLogoProps): UseCompanyLogoReturn => {
  const { themeName } = useTheme();
  const isCustomLogoPresent = !!themeData?.logo_url;
  // Use smaller dimensions for default theme
  const isDefaultTheme = themeName === ECompanyThemeName.DEFAULT;
  const shouldUseSmallLogo = isDefaultTheme && !isCustomLogoPresent && !isFullPageLoader;

  const getPredefinedThemeLogos = () => {
    const logoSrc = variant === LogoVariant.WHITE ? TelletLogoWhite : TelletLogo;

    return {
      [ECompanyThemeName.DEFAULT]: (
        <Image
          src={logoSrc}
          alt="logo"
          className={`${className} ${shouldUseSmallLogo ? 'w-[50px] h-auto' : ''}`}
        />
      ),
      [ECompanyThemeName.EMPATHY]: (
        <Image
          src={EmpathyLogo}
          alt="logo"
          className={`${className} ${shouldUseSmallLogo ? 'w-[120px] h-auto' : ''}`}
        />
      ),
    };
  };

  const logo = isCustomLogoPresent ? (
    <CustomLogo logoUrl={themeData.logo_url!} className={className} />
  ) : (
    getPredefinedThemeLogos()[themeName]
  );

  return {
    logo,
    isCustomLogoPresent,
  };
};

export default useCompanyLogo;
